import React from 'react';
import { AppSelect } from '../../../../components/common/select';
import { PERIOD_OPTIONS, PAYMENT_OPTIONS } from '../../constants/filterOptions';

const SalesListDetailFilter = ({
    filters,
    handleFilterChange,
    periodOptions = PERIOD_OPTIONS,
    salesCreatorOptions,
    serviceOptions,
    productCategoryOptions,
    standardOptions,
    departmentOptions,
    paymentOptions = PAYMENT_OPTIONS,
    branchOptions = [],
    taggingOptions = [],
    canViewGlobal,
    canViewDepartment,
    canViewOwn,
    user
}) => {
    // Determine if filters should be disabled based on permissions
    const isCreatorDisabled = !canViewGlobal && canViewOwn; // Only disable for view_own users
    const isDepartmentDisabled = !canViewGlobal && (canViewDepartment || canViewOwn); // Department is disabled for both department and own permissions

    // For department users, pre-select their department
    const effectiveDepartmentValue = canViewDepartment && !canViewGlobal && user?.departmentId
        ? user.departmentId.toString()
        : filters.departmentId;

    return (
        <>
            {/* Permission Notice */}
            {!canViewGlobal && (
                <div className="alert alert-info mb-3" role="alert">
                    <div className="d-flex">
                        <div>
                            <svg xmlns="http://www.w3.org/2000/svg" className="icon alert-icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                <circle cx="12" cy="12" r="9"></circle>
                                <line x1="12" y1="8" x2="12" y2="12"></line>
                                <line x1="12" y1="16" x2="12.01" y2="16"></line>
                            </svg>
                        </div>
                        <div>
                            <h4 className="alert-title">Limited Access</h4>
                            <div className="text-muted">
                                {canViewOwn && 'You can only view your own data. Some filters are disabled.'}
                                {canViewDepartment && !canViewOwn && 'You can only view data from your department. Department is pre-selected and creator filter is limited to your department.'}
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Select Periode */}
            <div className="mb-3">
                <label className="form-label">Select Periode</label>
                <AppSelect
                    name="period"
                    value={filters.period}
                    onChange={(name, value) => handleFilterChange('period', value)}
                    options={periodOptions}
                    placeholder="Select option"
                    valueField="id"
                    labelField="name"
                    label=""
                />
                {/* Display custom date range if applied */}
                {filters.period === 'custom' && filters.startDate && filters.endDate && (
                    <div className="mt-2">
                        <span>
                            {new Date(filters.startDate).toLocaleDateString()} - {new Date(filters.endDate).toLocaleDateString()}
                        </span>
                    </div>
                )}
            </div>

            {/* Department */}
            <div className="mb-3">
                <label className="form-label">Department</label>
                <AppSelect
                    name="departmentId"
                    value={Array.isArray(effectiveDepartmentValue) ? effectiveDepartmentValue : (effectiveDepartmentValue ? [effectiveDepartmentValue] : [])}
                    onChange={(name, value) => handleFilterChange('departmentId', value)}
                    options={departmentOptions}
                    placeholder="Select option(s)"
                    valueField="id"
                    labelField="name"
                    label=""
                    disabled={isDepartmentDisabled}
                    multiple={true}
                />
                {isDepartmentDisabled && (
                    <small className="form-hint">
                        {canViewDepartment && !canViewOwn && 'Pre-selected to your department'}
                        {canViewOwn && 'Restricted to your own data'}
                    </small>
                )}
            </div>

            {/* Sales Creator */}
            <div className="mb-3">
                <label className="form-label">Sales Creator</label>
                <AppSelect
                    name="salesCreator"
                    value={filters.salesCreator || []}
                    onChange={(name, value) => handleFilterChange('salesCreator', value)}
                    options={salesCreatorOptions}
                    placeholder="Select option(s)"
                    valueField="id"
                    labelField="name"
                    label=""
                    disabled={isCreatorDisabled}
                    multiple={true}
                />
                {isCreatorDisabled && (
                    <small className="form-hint">
                        {canViewOwn && 'Restricted to your own data'}
                    </small>
                )}
                {canViewDepartment && !canViewOwn && !isCreatorDisabled && (
                    <small className="form-hint">
                        Limited to creators from your department
                    </small>
                )}
            </div>

            {/* Service */}
            <div className="mb-3">
                <label className="form-label">Service</label>
                <AppSelect
                    name="serviceCategory"
                    value={filters.serviceCategory || []}
                    onChange={(name, value) => handleFilterChange('serviceCategory', value)}
                    options={serviceOptions}
                    placeholder="Select Option(s)"
                    valueField="id"
                    labelField="name"
                    label=""
                    multiple={true}
                />
            </div>

            {/* Product Category */}
            <div className="mb-3">
                <label className="form-label">Product Category</label>
                <AppSelect
                    name="productCategory"
                    value={filters.productCategory || []}
                    onChange={(name, value) => handleFilterChange('productCategory', value)}
                    options={productCategoryOptions}
                    placeholder="Select Option(s)"
                    valueField="id"
                    labelField="name"
                    label=""
                    multiple={true}
                />
            </div>

            {/* Standard */}
            <div className="mb-3">
                <label className="form-label">Standard</label>
                <AppSelect
                    name="standard"
                    value={filters.standard || []}
                    onChange={(name, value) => handleFilterChange('standard', value)}
                    options={standardOptions}
                    placeholder="Select Option(s)"
                    valueField="id"
                    labelField="name"
                    label=""
                    multiple={true}
                />
            </div>

            {/* Payment */}
            <div className="mb-3">
                <label className="form-label">Payment</label>
                <AppSelect
                    name="payment"
                    value={filters.payment || []}
                    onChange={(name, value) => handleFilterChange('payment', value)}
                    options={paymentOptions}
                    placeholder="Select Option(s)"
                    valueField="id"
                    labelField="name"
                    label=""
                    multiple={true}
                />
            </div>

            {/* Branch */}
            <div className="mb-3">
                <label className="form-label">Branch</label>
                <AppSelect
                    name="branchId"
                    value={filters.branchId || ''}
                    onChange={(name, value) => handleFilterChange('branchId', value || '')}
                    options={[{ id: '', name: 'All Branch' }, ...branchOptions]}
                    placeholder="All Branch"
                    valueField="id"
                    labelField="name"
                    label=""
                />
            </div>

            {/* Tagging */}
            <div className="mb-3">
                <label className="form-label">Tagging</label>
                <AppSelect
                    name="taggingIds"
                    value={Array.isArray(filters.taggingIds) ? filters.taggingIds : []}
                    onChange={(name, value) => handleFilterChange('taggingIds', value)}
                    options={taggingOptions}
                    placeholder="Select Option(s)"
                    valueField="id"
                    labelField="name"
                    label=""
                    multiple={true}
                />
            </div>
        </>
    );
};

export default SalesListDetailFilter;

